import type { AggregateView, NavItemView, PublicSectionView } from '@/api/types';

const LEGACY_HOME_SECTION_TYPES = new Set(['problem', 'method', 'innovation', 'impact']);
const HIDDEN_NAV_TARGETS = new Set([
  '#problem',
  '#method',
  '#innovation',
  '#impact',
  'module:achievement-intro/intro',
  'module:application-form/period',
  'module:application-form/team',
  'module:application-form/organization',
  'module:application-form/recommendation',
]);
const HIDDEN_APPLICATION_FORM_SECTION_TITLES = new Set([
  '起止时间',
  '主要完成人贡献',
  '主要完成人情况',
  '主要完成单位贡献',
  '主要完成单位情况',
  '推荐意见',
]);
const MODULE_LINK_SECTION_CONFIGS = [
  {
    id: '71000000-0000-0000-0000-000000000021',
    title: '申报书',
    routeName: 'application-form',
    sort: 40,
  },
  {
    id: '71000000-0000-0000-0000-000000000022',
    title: '佐证材料',
    routeName: 'support-material',
    sort: 50,
  },
  {
    id: '71000000-0000-0000-0000-000000000023',
    title: '成果总结报告',
    routeName: 'summary-report',
    sort: 55,
  },
] as const;
export function transformAggregate(data: AggregateView): AggregateView {
  return {
    ...data,
    sections: transformSections(data.sections),
    navItems: transformNavItems(data.navItems),
  };
}

function transformSections(sections: readonly PublicSectionView[]): readonly PublicSectionView[] {
  const visibleSections = sections
    .filter((section) => !LEGACY_HOME_SECTION_TYPES.has(section.type))
    .filter((section) => !isHiddenModuleSection(section))
    .map(renameModuleSection);

  if (visibleSections.some((section) => section.type === 'module-links')) {
    return visibleSections;
  }
  return [...visibleSections, ...createModuleLinkSections()];
}

function createModuleLinkSections(): readonly PublicSectionView[] {
  const now = new Date().toISOString();
  return MODULE_LINK_SECTION_CONFIGS.map((config) => ({
    id: config.id,
    type: 'module-links',
    title: config.title,
    summary: config.routeName,
    bodyHtml: '',
    cover: null,
    videoUrl: null,
    posterUrl: null,
    images: [],
    materials: [],
    sidebar: [],
    sort: config.sort,
    publishStatus: 'Published',
    createdAt: now,
    updatedAt: now,
  }));
}

function renameModuleSection(section: PublicSectionView): PublicSectionView {
  if (section.type !== 'module:support-material') {
    return section;
  }
  return {
    ...section,
    sort: supportMaterialSort(section),
  };
}

function supportMaterialSort(section: PublicSectionView): number {
  if (section.type !== 'module:support-material') {
    return section.sort;
  }
  if (section.title === '课程资源佐证') {
    return 1310;
  }
  return section.title === '机制建设佐证' ? 1320 : section.sort;
}

function isHiddenModuleSection(section: PublicSectionView): boolean {
  if (section.type !== 'module:application-form') {
    return false;
  }
  return HIDDEN_APPLICATION_FORM_SECTION_TITLES.has(section.title);
}

function transformNavItems(navItems: readonly NavItemView[]): readonly NavItemView[] {
  return navItems
    .filter((item) => !HIDDEN_NAV_TARGETS.has(item.targetAnchor))
    .map(transformNavItem);
}

function transformNavItem(item: NavItemView): NavItemView {
  const children = Array.isArray(item.children) ? item.children : null;
  if (children === null) {
    return item;
  }
  return {
    ...item,
    children: transformModuleChildren(children),
  };
}

function transformModuleChildren(
  children: readonly NavItemView[],
): readonly NavItemView[] {
  return children.filter((child) => !HIDDEN_NAV_TARGETS.has(child.targetAnchor));
}
